// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@environment';

/**
 * DTO for a single committee member from the API
 */
export interface CommitteeMemberDto {
  name: string;
  logoUrl: string;
  title: string;
  role: string;
  appointedBy: string;
  votingStatus: string;
  canEdit: boolean;
  committeeName: string;
  userId: string;
  email: string;
  projectId: string;
  committeeId: string;
  committeeMemberId: string;
  organization?: string;
}

/**
 * A group of committee members under the same committee
 */
export interface CommitteeGroup {
  name: string;
  members: CommitteeMemberDto[];
}

/**
 * Response for the grouped committee members endpoint
 */
export interface GroupedCommitteeMembersResponse {
  committees: CommitteeGroup[];
}

/**
 * Request payload for reassigning a committee member
 */
export interface ReassignMemberRequest {
  newEmail: string;
  firstName: string;
  lastName: string;
  committeeId: string;
  projectId: string;
  role: string;
  votingStatus: string;
}

/**
 * Response for the reassign member endpoint
 */
export interface ReassignMemberResponse {
  success: boolean;
  message: string;
  data?: {
    email: string;
    firstName: string;
    lastName: string;
    projectId: string;
    committeeId: string;
    role: string;
    votingStatus: string;
  };
}

/**
 * DTO for employees with valid organization domain
 */
export interface ValidDomainEmployeeDto {
  userId: string;
  name: string;
  email: string;
  avatarUrl?: string;
}

/**
 * Response for the valid domain employees endpoint
 */
export interface ValidDomainEmployeesResponse {
  employees: ValidDomainEmployeeDto[];
}

/**
 * Response for the committee member ID lookup
 */
export interface GetCommitteeMemberIdResponse {
  committeeMemberId: string;
}

/**
 * CommitteeMembersRestService
 *
 * Frontend service that calls the new REST endpoints for committee members.
 * All business logic has been moved to the backend.
 * This service is responsible only for HTTP communication.
 */
@Injectable({
  providedIn: 'root',
})
export class CommitteeMembersRestService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}`;

  /**
   * Get committee members for a project, already grouped by committee name.
   * All data transformations are done server-side.
   *
   * @param orgId - Organization ID (Salesforce B2B Account ID)
   * @param projectId - Project/Foundation ID
   * @param isAllOrgs - Whether to include all organizations in rollup
   * @param bustCache - Whether to add cache-busting parameter (for refresh after updates)
   */
  getCommitteeMembers(
    orgId: string,
    projectId: string,
    isAllOrgs = false,
    bustCache = false
  ): Observable<GroupedCommitteeMembersResponse> {
    let params = new HttpParams().set('isAllOrgs', isAllOrgs.toString());

    // Add cache-busting parameter to force fresh data after updates
    if (bustCache) {
      params = params.set('_t', Date.now().toString());
    }

    return this.http.get<GroupedCommitteeMembersResponse>(
      `${this.baseUrl}/orgs/${orgId}/projects/${projectId}/committee-members`,
      { params }
    );
  }

  /**
   * Reassign a committee member to a new person.
   * Validation is done server-side.
   *
   * @param orgId - Organization ID
   * @param memberId - Current committee member ID to be replaced
   * @param request - New member details
   */
  reassignMember(
    orgId: string,
    memberId: string,
    request: ReassignMemberRequest
  ): Observable<ReassignMemberResponse> {
    return this.http.post<ReassignMemberResponse>(
      `${this.baseUrl}/orgs/${orgId}/committee-members/${memberId}/reassign`,
      request
    );
  }

  /**
   * Get employees with valid organization domain.
   * Domain filtering is done server-side.
   *
   * @param orgId - Organization ID
   * @param primaryDomain - Organization's primary domain
   * @param domainAlias - Optional comma-separated domain aliases
   */
  getValidDomainEmployees(
    orgId: string,
    primaryDomain: string,
    domainAlias = ''
  ): Observable<ValidDomainEmployeesResponse> {
    let params = new HttpParams().set('primaryDomain', primaryDomain);

    if (domainAlias) {
      params = params.set('domainAlias', domainAlias);
    }

    return this.http.get<ValidDomainEmployeesResponse>(
      `${this.baseUrl}/orgs/${orgId}/employees/valid-domain`,
      { params }
    );
  }

  /**
   * Get committeeMemberId on-demand (lazy fetch).
   * Called only when user initiates an edit action to avoid N API calls on page load.
   *
   * @param orgId - Organization ID
   * @param userId - User ID of the committee member
   * @param committeeId - Committee ID
   * @param projectId - Project ID
   */
  getCommitteeMemberId(
    orgId: string,
    userId: string,
    committeeId: string,
    projectId: string
  ): Observable<GetCommitteeMemberIdResponse> {
    const params = new HttpParams()
      .set('committeeId', committeeId)
      .set('projectId', projectId);

    return this.http.get<GetCommitteeMemberIdResponse>(
      `${this.baseUrl}/orgs/${orgId}/users/${userId}/committee-member-id`,
      { params }
    );
  }
}
